import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Clock, Shield, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Constants for intervals and timeouts
const TIME_UPDATE_INTERVAL_MS = 60000; // 1 minute
const HEARTBEAT_INTERVAL_MS = 30000;   // 30 seconds

const StreamPage = () => {
  const [accessData, setAccessData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [loading, setLoading] = useState(true);
  const [videoId, setVideoId] = useState('');
  const [videoHash, setVideoHash] = useState('');
  const [connectionError, setConnectionError] = useState(false);
  const navigate = useNavigate();

  const validateAccess = useCallback(async () => {
    setConnectionError(false);
    try {
      // Get stored access data
      const stored = localStorage.getItem('streamAccess');
      
      if (!stored) {
        toast.error('No stream access found. Please purchase access first.');
        navigate('/');
        return;
      }

      const storedData = JSON.parse(stored);
      
      // Validate with backend
      const response = await fetch(`${API_URL}/api/stream/validate-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storedData.accessToken}`
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Access validation failed');
      }
      
      if (!data.valid) {
        throw new Error('Access expired or invalid');
      }

      setAccessData({
        email: data.email,
        expiresAt: data.expires_at,
        timeRemaining: data.time_remaining
      });
      setVideoId(data.video_id);
      setVideoHash(data.video_hash || '');
      setTimeRemaining(data.time_remaining);
      setLoading(false);

    } catch (error) {
      console.error('Access validation error:', error);
      
      // Check if it's a revocation, expiry, or network issue
      const errorMessage = error.message?.toLowerCase() || '';
      
      if (errorMessage.includes('revoked') || errorMessage.includes('new link')) {
        // Access was revoked - fresh link sent
        localStorage.removeItem('streamAccess');
        toast.info('Your access has been refreshed. Check your email for a new link!');
        navigate('/');
      } else if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        // Definitely expired - clear and redirect
        localStorage.removeItem('streamAccess');
        toast.error('Your 48-hour access has expired. Please purchase again to continue watching.');
        navigate('/');
      } else {
        // Network or other error - show retry option, don't clear token
        setConnectionError(true);
        toast.error('Connection issue. Please try again.');
        setLoading(false);
      }
    }
  }, [navigate]);

  useEffect(() => {
    let hasValidated = false;
    
    const runValidation = async () => {
      if (hasValidated) return;
      hasValidated = true;
      await validateAccess();
    };
    
    runValidation();
  }, [validateAccess]);

  // Update time remaining every minute
  useEffect(() => {
    if (!accessData) return;

    const updateTimeRemaining = () => {
      const expiresAt = new Date(accessData.expiresAt);
      const now = new Date();
      const diff = expiresAt - now;

      if (diff <= 0) {
        setTimeRemaining('Expired');
        localStorage.removeItem('streamAccess');
        toast.warning('Your stream access has expired');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeRemaining(`${hours}h ${minutes}m remaining`);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, TIME_UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [accessData, navigate]);

  // Heartbeat to keep session active and check for revocation
  useEffect(() => {
    if (!accessData) return;

    const heartbeat = async () => {
      try {
        const stored = localStorage.getItem('streamAccess');
        if (!stored) return;
        
        const storedData = JSON.parse(stored);
        const response = await fetch(`${API_URL}/api/stream/validate-access`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${storedData.accessToken}`
          },
        });
        
        const data = await response.json();
        
        // Check if access was revoked (e.g., fresh link sent by Tezza)
        if (!response.ok || !data.valid) {
          localStorage.removeItem('streamAccess');
          toast.error('Your access has been updated. A new link has been sent to your email.');
          navigate('/');
          return;
        }
        
        // Update time remaining
        if (data.time_remaining) {
          setTimeRemaining(data.time_remaining);
        }
      } catch (error) {
        console.warn('Heartbeat failed:', error);
      }
    };

    // Send heartbeat to detect access revocation quickly
    const interval = setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [accessData, navigate]);

  if (loading) {
    return (
      <div className="stream-loading" data-testid="stream-loading">
        <Loader2 className="h-12 w-12 animate-spin text-electric-blue" />
        <p>Validating your access...</p>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="stream-loading" data-testid="stream-error">
        <AlertCircle className="h-12 w-12 text-yellow-500" />
        <p>Connection issue. Your access is still valid.</p>
        <Button 
          onClick={() => {
            setLoading(true);
            validateAccess();
          }}
          className="retry-button"
          data-testid="retry-button"
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="stream-page" data-testid="stream-page">
      {/* Header */}
      <header className="stream-header">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="back-button"
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Home
        </Button>

        <div className="stream-info">
          <div className="info-item">
            <Clock className="h-4 w-4" />
            <span data-testid="time-remaining">{timeRemaining}</span>
          </div>
          <div className="info-item">
            <Shield className="h-4 w-4" />
            <span>Secure Stream</span>
          </div>
        </div>
      </header>

      {/* Video Player */}
      <div className="stream-container">
        <div className="stream-wrapper">
          <iframe
            src={`https://player.vimeo.com/video/${videoId}?h=${videoHash}&autoplay=1&title=0&byline=0&portrait=0`}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Washed Away"
            className="stream-iframe"
            data-testid="stream-video"
          ></iframe>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="warning-banner">
        <AlertCircle className="h-5 w-5" />
        <p>
          This stream is monitored and watermarked. Sharing access or recording this content 
          is a violation of your purchase terms and Australian copyright law.
        </p>
      </div>

      {/* Stream Details */}
      <div className="stream-details">
        <div className="details-card">
          <h2 className="details-title">Your Stream Ticket</h2>
          <div className="details-list">
            <div className="detail-item">
              <span className="detail-label">Email:</span>
              <span className="detail-value" data-testid="access-email">{accessData.email}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Expires:</span>
              <span className="detail-value" data-testid="access-expires">
                {new Date(accessData.expiresAt).toLocaleDateString()} at{' '}
                {new Date(accessData.expiresAt).toLocaleTimeString()}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Access Type:</span>
              <span className="detail-value">Single-device, Non-transferable</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreamPage;
